<template>
	<div>
		<div class="row">
			<div class="col-12 mb-4">
				<h1 v-if="isNew">{{ $t("add-room") }}</h1>
				<h1 class="float-left" v-else-if="loaded">{{ room.name }}</h1>
				<div class="skeleton midnight h16 w-half" v-else></div>

				<button class="btn btn-primary float-right" @click="finishEditing">
					{{ $t("save") }}
				</button>
			</div>
			<div class="col-12" v-if="success">
				<b-alert variant="success" show>
					{{ $t("saved") }}
				</b-alert>
			</div>

			<div class="col-12" v-if="error">
				<b-alert variant="danger" show>{{ error }}</b-alert>
			</div>

			<div class="col-12 mb-2">
				<label >{{ $t("name") }}</label>
				<b-form-input v-model="room.name" v-if="loaded" />
				<div class="skeleton midnight h16 w-half" v-else></div>
			</div>

			<div class="col-12 mb-2">
				<label >{{ $t("capacity") }}</label>
				<b-form-input v-model="room.capacity" v-if="loaded" />
				<div class="skeleton midnight h16 w-half" v-else></div>
			</div>

			<div class="col-12 mb-2">
				<label >{{ $t("clinical-capacity") }}</label>
				<b-form-input v-model="room.clinicalcapacity" v-if="loaded"/>
				<div class="skeleton midnight h16 w-half" v-else></div>
			</div>

			<div class="col-12 mb-2">
				<SelectInput
					:label="$t('building')"
					:options="buildingOptions"
					optionLabel="name"
					optionValue="id"
					v-model="room.ttbuilding_id"
					v-if="loaded"
				>
				</SelectInput>

				<div class="skeleton midnight h16 w-half" v-else></div>
				<div class="btn-group btn-group-toggle" style="display: block" v-if="room.ttbuilding_id">
					<button
						class="btn btn-secondary"
						:class="{
							'btn-on': useBuildingLocation,
							'btn-off': !useBuildingLocation
						}"
						@click="useBuildingLocation=!useBuildingLocation"
					>
						{{ $t('use-building-location') }}
					</button>
					<button
						class="btn btn-secondary"
						:class="{
							'btn-on': !useBuildingLocation,
							'btn-off': useBuildingLocation
						}"
						@click="useBuildingLocation=!useBuildingLocation"
					>
						{{ $t('specify-location') }}
					</button>
				</div>
			</div>
			<div class="col-12 mb-2" v-if="loaded && (!room.ttbuilding_id || !useBuildingLocation)" >
				<LatLong
					:confidenceMargin="10"
					@change="handleData"
					:location="room"
				>
				</LatLong>
			</div>

			<div class="col-12 mb-2">
				<label class="font-bold">{{ $t("tags") }}</label>
				<TagPicker
					:tags="room.tags"
				/>
			</div>
			<div class="col-12 mb-8">
				<label class="font-bold mb-4">{{ $t("teachers") }}</label>
				<button class="btn btn-secondary float-right" @click="addTeacherRota()"><b-icon icon="plus" /> {{ $t('add') }}</button>
				<div class="list-group mt-1" v-for="(teacherrota, index) in room.teacherrota" :key="index">
					<div class="list-group-item">
						<div class="row">
							<div class="col-6 col-md-4">
								<span class="text-sm font-weight-bold">{{$t('from')}}</span> 
								<input v-if="teacherrota.id==null" type="date" v-model="teacherrota.datefrom" class="form-control">
								<p v-else-if="loaded" class="mb-0">{{ $datetime.getLocaleStringFromISODate(teacherrota.datefrom) }}</p>
							</div>
							<div class="col-6 col-md-4">
								<span class="text-sm font-weight-bold">{{$t('to')}}</span> 

								<input v-if="teacherrota.id==null" type="date" v-model="teacherrota.dateto" class="form-control">
								<p v-else-if="loaded" class="mb-0">{{ $datetime.getLocaleStringFromISODate(teacherrota.dateto) }}</p>
							</div>
						</div>
					</div>
					<div class="list-group-item">
						<div class="row">
							<div class="col-md-2">
								<span class="font-bold">{{ $t("session-type") }}</span>
							</div>
							<div class="col-md-2">
								<span class="font-bold">{{ $t("day") }}</span>
							</div>
							<div class="col-md-2">
								<span class="font-bold">{{ $t("period") }}</span>
							</div>
							<div class="col-md-6">
								<span class="font-bold">{{ $t("teacher") }}</span>
								<button class="btn btn-secondary float-right" @click="addTeacherTemplate(teacherrota)"><b-icon icon="plus" /> {{ $t('add') }}</button>
							</div>
						</div>
					</div>
					<template v-for="(teacher, tkey) in teacherrota.rules">
						<div class="list-group-item" v-bind:key="tkey">
							<div class="row">
								<div class="col-md-2">
									{{ sessiontypes[teacher.ttsessiontype_id] }}
								</div>
								<div class="col-md-2">
									<select v-if="!teacher.ttsessiontype_id" class="form-control my-2"  v-model="teacher.dayofweek">
										<option value="">{{$t('select')}}</option>
										<option v-for="index in 7" :key="index" :value="index">{{ $t('dayname-long-' + (index)) }}</option>
									</select>
									<template v-else>{{ $t('dayname-long-' + teacher.dayofweek) }}</template>
								</div>
								<div class="col-md-2">
									<select v-if="!teacher.ttsessiontype_id" class="form-control my-2" v-model="teacher.tttemplate_id">
										<option value="">{{$t('select')}}</option>
										<option v-for="template in templates" :key="template.tttemplate_id" :value="template.tttemplate_id">{{ template.tttemplate_name }}</option>
									</select>
									<template v-else>
										<template v-for="template in templates">
											<template v-if="template.tttemplate_id == teacher.tttemplate_id">{{template.tttemplate_name}}</template>
										</template>
									</template>
								</div>
								<div class="col-md-4">
									<div>
										<span
											class="badge badge-secondary badge-sm mr-2"
											v-for="(user, index) in teacher.users"
											v-bind:key="index"
										>
											{{ user.displayName }}
											<a @click="removeTeacher(teacher.users, index)"><b-icon icon="x"></b-icon></a>
										</span>
									</div>
									
									<span
										class="badge badge-primary badge-sm mr-2"
										@click="openTeacherModal(teacher.users)"
									>
										<b-icon icon="plus"></b-icon> {{ teacher.users.length ? $t("add") : $t('teacher') }}
									</span>
									<span
										class="badge badge-danger badge-sm mr-2"
										v-if="teacher.users.length"
										@click="teacher.users=[]"
									>
										<b-icon icon="x"></b-icon> {{ $t('clear-all') }}
									</span>
								</div>
								<div class="col-md-2 text-right">
									<button v-if="!teacher.ttsessiontype_id" class="btn btn-sm btn-outline-danger" @click="removeTeacherTemplate(teacherrota, tkey)"><b-icon icon="trash" /> {{ $t('remove') }}</button>
								</div>
							</div>
						</div>
					</template>
				</div>

			</div>
		</div>


	</div>
</template>

<script>
import TagPicker from "src/components/Tags/TagPicker";
import LatLong from '/src/components/LatLong.vue';
export default {
	components: {
		TagPicker,
		LatLong
	},
	data: function () {
		return {
			success: false,
			error: null,
			editing: false,
			loaded: false,
			buildingOptions: [],
			sessiontypes: {} ,
			templates: [],
			room: {
				name: '' ,
				address1: null ,
				address2: null ,
				address3: null ,
				address4: null ,
				address5: null ,
				postcode: null ,
				long: null ,
				lat: null ,
				capacity: null ,
				clinicalcapacity: null ,
				ttbuilding_id: null,
				teachers: [],
				tags: [],
				teacherrota: [],
			},
			useBuildingLocation: false,
		};
	},
	mounted() {
		this.getBuildings();
		this.getSessionTypes();
		this.getTemplates();
		this.getTeachers();
		if (!this.$route.params["roomid"]) {
			// add mode
			this.loaded = true;
		} else {
			// view mode
			this.loadRoom(this.$route.params["roomid"]);
		}
	},
	methods: {
		getBuildings() {
			this.buildingoptions = [
				{ value: null, text: '' }
			]
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/ttbuilding/list`).then((res) => {
				this.buildingOptions=res.results;
			});
		},
		getSessionTypes: function () {
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/ttsessiontype/list`).then((res) => {
				if (res.status === "SUCCESS") {
					res.results.forEach(item => {
						this.sessiontypes[item.ttsessiontype_id] = item.ttsessiontype_name;
					});
				}
			});
		},
		getTemplates: function() {
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/tttemplate/list`)
				.then(res => {
					if (res.status === "SUCCESS") {
						this.templates = res.results;
					} else {
						if (res.error_message) {
							this.error = res.error_message;
						} else {
							this.error = this.$t("failed-load-templates");
						}
					}
				})
				.catch(() => {
					this.error = this.$t("failed-load-templates");
				})
		},
		getTeachers: function() {
			this.$users.getTeachers.call(this).then(json => {
				this.teachers = json;
			});
		},
		startEditing: function () {
			this.editing = true;
			this.success = false;
		},
		finishEditing: function () {
			this.saveRoom();
		},
		loadRoom: function (roomId) {
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/session-setup/room/get`, {
				data: {
					id: roomId,
				},
			}).then((res) => {
				if (res.status === "SUCCESS") {
					this.room = res.result;
					if(this.room.ttbuilding_id && !this.room.postcode && !this.room.address1){
						this.useBuildingLocation = true
					}
					this.loaded = true;
				}
			});
		},
		saveRoom: function () {
			this.$store.commit("showBlockingSpinner");
			this.$cfetch(`${this.$settings.APIBaseURL}/timetable/session-setup/room/save`, {
				method: "post",
				data: this.room,
			}).then((res) => {
				this.$store.commit("hideBlockingSpinner");
				if (res.status === "SUCCESS") {
					this.room = res.result;
					this.editing = false;
					this.success = true;
				} else {
					if (res.error_message) {
						this.error = res.error_message;
					} else {
						this.error = this.$t("error-saving-room");
					}
				}
			}).catch(error => {
				this.$store.commit("hideBlockingSpinner");
				this.$log.error.call(this, error);
				this.error = this.$t("error-saving-room");
			});
		},
		addTeacherRota(){
			this.room.teacherrota.push({
				id: null,
				datefrom: null,
				dateto: null,
				rules: []
			});
		},
		addTeacherTemplate(teacherrota){
			teacherrota.rules.push({
				users: [],
				ttroom_id: null,
				tttemplate_id: null,
				dayofweek: null
			});
		},
		openTeacherModal(teachers) {
			const self = this;
			this.$store.commit("openModal", {
				type: "selector",
				title: this.$t("teacher"),
				selectCallback: function(selection) {
					const isSelected = teachers.find((teacher, i) => {
						if (teacher.id === selection.id) {
							self.$delete(teachers, i);
							return true;
						}
					});
					if (!isSelected) {
						teachers.push(selection);
					}
					self.recipientsError = false;
				},
				selected: teachers,
				data: this.teachers,
				captionKey: "displayName"
			});
		},
		removeTeacherTemplate(teacherrota, index){
			teacherrota.rules.splice(index, 1);
			return true;
		},
		removeTeacher(users, selection) {
			users.splice(selection, 1);
			return true;
		},
		handleData(data){
			const { coordinates, address } = data;
			Object.assign(this.room, {
				lat: coordinates.lat,
				long: coordinates.lng,
				address1: address.address1,
				address2: address.address2,
				address3: address.address3,
				address4: address.address4,
				address5: address.address5,
				postcode: address.postcode,
				country: address.country
			});
			this.confidencePassed = data.confidencePassed
		},
	},
	computed: {
		isNew: function () {
			return !this.$route.params["roomid"];
		},
		currentBuilding: function () {
			var building = {};
			this.buildingOptions.forEach(location => {
				if(location.id !==null && location.id == this.room.ttbuilding_id){
					building = location;
				} else { // In case we are adding a new room without building association
					building = this.room
				}
			});

			return building;
		}
	}
};
</script>
